import React, { useState, useEffect } from "react";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import LoginHeader from "../components/LoginHeader.js";
import SideNavigation from "../components/SideNavigation.js";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Button from "@mui/material/Button";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import EastIcon from "@mui/icons-material/East";
import { useNavigate, useLocation } from "react-router-dom";
import { BlobServiceClient } from "@azure/storage-blob";
import CryptoJS from "crypto-js";
import { MultiSelect } from "primereact/multiselect";
import { Dropdown } from "primereact/dropdown";
import { DatePicker } from "antd";
import { BiSolidCalendar } from "react-icons/bi";
import dayjs from "dayjs";
import AADHeader from "../components/AADHeader.js";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import Tooltip from "@mui/material/Tooltip";
import InfoIcon from "@mui/icons-material/Info";
import "./brsr.css";
import { saveSectionData, updateStep4WithSectionA } from "./utils/answerManager";
import { coerceLegalEntities } from "../utils/s1ApiNormalize";
import usePermissions from "../hooks/usePermissions";
import { PERMISSIONS } from "../constants/permissions";
import PermissionTooltip from "../components/PermissionTooltip";

const steps = ["Start", "Repository", "Input", "Report"];

/** Prefer first non-empty value (skips null/undefined/"" / empty arrays). */
function pickFirstValue(...values) {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    if (typeof value === "string" && !value.trim()) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    return value;
  }
  return "";
}

export default function BrsrNewReport() {
  const username = localStorage.getItem("userName");
  const companyid = localStorage.getItem("companyid");
  const { has: hasPermission } = usePermissions();
  const canStartAssessment = hasPermission(PERMISSIONS.START_ASSESSMENTS);
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isEditMode = searchParams.get("mode") === "edit";
  const editReportId = searchParams.get("reportId");
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [loading, setLoading] = useState(false);
  const [savedReports, setSavedReports] = useState([]);
  const [formData, setFormData] = useState({
    reportName: "",
    completionTimeline: "",
    legalEntity: "",
    financialYear: "",
    reportingOn: "BRSR + Core",
    revenuePreviousFY: "",
    revenueCurrentFY: "",
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [name, setName] = useState("");
  const [timeline, setTimeline] = useState(null);
  const [legalEntity, setLegalEntity] = useState("");
  const [activelegalentity, setActiveLegalEntity] = useState("");
  const [financialYear, setFinancialYear] = useState("");
  const [reportingOn, setReportingOn] = useState("");
  const [revenuePreviousFY, setRevenuePreviousFY] = useState("");
  const [revenueCurrentFY, setRevenueCurrentFY] = useState("");
  const [errors, setErrors] = useState({});
  const [reportId, setReportId] = useState("");
  const [existingReports, setExistingReports] = useState([]);
  // When edit-loaded FY falls outside the rolling 3-option window, keep it visible in the dropdown.
  const [extraFinancialYearOption, setExtraFinancialYearOption] = useState(null);

  // Calculate financial year options (Previous, Current, Next)
  const getFinancialYearOptions = () => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth(); // 0-11 (Jan = 0, Dec = 11)
    const currentYear = currentDate.getFullYear();

    // Financial year in India is April to March
    // If current month is Jan-Mar, FY starts from previous year
    // If current month is Apr-Dec, FY starts from current year
    let currentFYStartYear;
    if (currentMonth < 3) {
      // Jan, Feb, Mar
      currentFYStartYear = currentYear - 1;
    } else {
      // Apr to Dec
      currentFYStartYear = currentYear;
    }

    const previousFY = {
      label: `FY ${currentFYStartYear - 1}-${String(currentFYStartYear).slice(-2)}`,
      value: `${currentFYStartYear - 1}-${String(currentFYStartYear).slice(-2)}`,
    };

    const currentFY = {
      label: `FY ${currentFYStartYear}-${String(currentFYStartYear + 1).slice(-2)}`,
      value: `${currentFYStartYear}-${String(currentFYStartYear + 1).slice(-2)}`,
    };

    const nextFY = {
      label: `FY ${currentFYStartYear + 1}-${String(currentFYStartYear + 2).slice(-2)}`,
      value: `${currentFYStartYear + 1}-${String(currentFYStartYear + 2).slice(-2)}`,
    };

    return [previousFY, currentFY, nextFY];
  };

  const financialYearOptions = (() => {
    const base = getFinancialYearOptions();
    if (!extraFinancialYearOption?.value) return base;
    if (base.some((opt) => opt.value === extraFinancialYearOption.value)) return base;
    return [...base, extraFinancialYearOption];
  })();

  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  useEffect(() => {
    if (isEditMode) return;
    const step1 = localStorage.getItem("step1");
    if (step1 === undefined || step1 === null) {
      console.log("Step1 is empty");
    } else {
      const dataStep1 = JSON.parse(step1);
      setName(dataStep1.name);
      if (dataStep1.timeline1 !== "" && dataStep1.timeline1 !== null) {
        const timelineDate = dayjs(dataStep1.timeline1);
        setTimeline(timelineDate);
        setSelectedDate(timelineDate);
      }
      setLegalEntity(dataStep1.legalEntity);
      if (dataStep1.financialYear) {
        setFinancialYear(dataStep1.financialYear);
      }
      if (dataStep1.revenuePreviousFY) {
        setRevenuePreviousFY(dataStep1.revenuePreviousFY);
      }
      if (dataStep1.revenueCurrentFY) {
        setRevenueCurrentFY(dataStep1.revenueCurrentFY);
      }
    }
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsSidebarOpen(false);
      } else {
        setIsSidebarOpen(true);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Edit mode: load saved BRSR data.json from blob and prefill the start form.
  useEffect(() => {
    const loadBrsrForEdit = async () => {
      if (!isEditMode || !editReportId) return;
      setLoading(true);

      try {
        const res = await fetch(`${process.env.REACT_APP_BASE_API_URL}GetSASToken`, {
          method: "GET",
          headers: {
            "Content-type": "application/json; charset=UTF-8",
          },
        });

        if (res.status !== 200) {
          console.error("Failed to fetch SAS token for BRSR edit");
          return;
        }

        const responseJson = await res.json();
        const connectionString = responseJson.data;
        // Fetch metadata row first (table storage via allreports) to prefill Start fields
        // when blob top-level Start keys are missing.
        let metadataRow = null;
        try {
          const reportTypeQueries = ["BRSR", "BRSR_CORE", "BRSR Core", "BRSR-CORE", ""];
          const metadataResults = await Promise.all(
            reportTypeQueries.map((t) => {
              const url = t ? `${process.env.REACT_APP_BASE_API_URL}allreports?reportType=${encodeURIComponent(t)}` : `${process.env.REACT_APP_BASE_API_URL}allreports`;
              return fetch(url, {
                method: "GET",
                headers: {
                  "Content-type": "application/json; charset=UTF-8",
                  companyid: companyid,
                  username: username,
                },
              })
                .then((r) => (r.status === 200 ? r.json() : null))
                .catch(() => null);
            }),
          );

          const mergedRows = [];
          const seenKeys = new Set();
          metadataResults.forEach((payload) => {
            if (!payload?.success || !Array.isArray(payload?.data)) return;
            payload.data.forEach((row) => {
              const key = String(row?.rowKey || row?.reportId || "");
              if (!key || seenKeys.has(key)) return;
              seenKeys.add(key);
              mergedRows.push(row);
            });
          });

          metadataRow = mergedRows.find((row) => String(row?.rowKey || row?.reportId || "") === String(editReportId) || String(row?.reportId || "") === String(editReportId));
        } catch (metadataErr) {
          // Non-blocking: blob prefill still works without metadata fetch.
          console.warn("Unable to fetch metadata row for edit prefill:", metadataErr);
        }

        const blobServiceClient = new BlobServiceClient(connectionString);
        const containerClient = blobServiceClient.getContainerClient("ecoprismfilecontainer");

        const blobPath = `${companyid}/reports/brsr/${editReportId}/data.json`;
        const blobClient = containerClient.getBlobClient(blobPath);
        const downloadBlockBlobResponse = await blobClient.download(0);
        const blob = await downloadBlockBlobResponse.blobBody;
        const downloadedText = await blob.text();

        const jsonData = JSON.parse(downloadedText);

        setReportId(editReportId);
        localStorage.setItem("brsr_report_id", editReportId);

        // Start form prefers blob top-level keys; fall back to allreports metadata when
        // a prior Preview save wiped Start fields but left sectionA/B/C intact.
        const loadedName = pickFirstValue(jsonData?.name, metadataRow?.reportTitle);
        if (loadedName) {
          setName(loadedName);
          localStorage.setItem("report_name", loadedName);
        }

        const loadedTimelineRaw = pickFirstValue(jsonData?.timeline1, jsonData?.timeline, metadataRow?.completionTimeline, metadataRow?.timeline);
        let loadedTimeline1 = jsonData?.timeline1 || "";
        let loadedTimeline = jsonData?.timeline || "";
        if (loadedTimelineRaw) {
          const timelineDate = dayjs(loadedTimelineRaw);
          if (timelineDate.isValid()) {
            setTimeline(timelineDate);
            setSelectedDate(timelineDate);
            loadedTimeline1 = loadedTimeline1 || timelineDate.toISOString();
            loadedTimeline = loadedTimeline || timelineDate.format("YYYY-MM-DD");
          }
        }

        const loadedLegalEntity = coerceLegalEntities(pickFirstValue(jsonData?.legalEntity, metadataRow?.legalEntity));
        setLegalEntity(loadedLegalEntity);

        const loadedFinancialYear = pickFirstValue(jsonData?.financialYear, metadataRow?.financialYear, metadataRow?.reportingYear);
        if (loadedFinancialYear) {
          setFinancialYear(loadedFinancialYear);
          const baseFyOptions = getFinancialYearOptions();
          if (!baseFyOptions.some((opt) => opt.value === loadedFinancialYear)) {
            setExtraFinancialYearOption({
              label: `FY ${loadedFinancialYear}`,
              value: loadedFinancialYear,
            });
          }
        }

        const reportTypeFromMetadata = metadataRow?.reportType || metadataRow?.type || "";
        const reportingOnFromMetadata = metadataRow?.reportingOn || (reportTypeFromMetadata === "BRSR_CORE" ? "BRSR + Core" : reportTypeFromMetadata === "BRSR" ? "BRSR" : "");
        const reportingOnValue = pickFirstValue(reportingOnFromMetadata, jsonData?.reportingOn);
        if (reportingOnValue) setReportingOn(reportingOnValue);

        // Revenue prefill preference: metadata -> blob(new keys) -> blob(legacy keys).
        const revenuePrevious = metadataRow?.revenuePreviousFY ?? metadataRow?.revenuepreviousfy ?? jsonData?.revenuePreviousFY ?? jsonData?.revenue2024 ?? "";
        const revenueCurrent = metadataRow?.revenueCurrentFY ?? metadataRow?.revenuecurrentfy ?? jsonData?.revenueCurrentFY ?? jsonData?.revenue2025 ?? "";

        // Business rule: only BRSR + Core carries revenue.
        if (reportingOnValue === "BRSR + Core" || reportTypeFromMetadata === "BRSR_CORE") {
          setRevenuePreviousFY(String(revenuePrevious));
          setRevenueCurrentFY(String(revenueCurrent));
        } else {
          setRevenuePreviousFY("");
          setRevenueCurrentFY("");
        }

        // Persist values so other BRSR pages can read the autofilled state.
        const step1 = {
          name: loadedName,
          timeline: loadedTimeline,
          timeline1: loadedTimeline1,
          legalEntity: loadedLegalEntity,
          financialYear: loadedFinancialYear,
          reportingOn: reportingOnValue,
          revenuePreviousFY: reportingOnValue === "BRSR + Core" || reportTypeFromMetadata === "BRSR_CORE" ? String(revenuePrevious) : "",
          revenueCurrentFY: reportingOnValue === "BRSR + Core" || reportTypeFromMetadata === "BRSR_CORE" ? String(revenueCurrent) : "",
        };
        localStorage.setItem("step1", JSON.stringify(step1));

        // Save section data using report-specific keys
        const sectionAData = jsonData?.sectionA || {};
        localStorage.setItem("brsr_sectionA_data", JSON.stringify(sectionAData));

        // Also save to report-specific storage for proper isolation
        if (sectionAData && Object.keys(sectionAData).length > 0) {
          saveSectionData(editReportId, "A", sectionAData);
          // Generate full step4 entries from section data
          updateStep4WithSectionA(editReportId, sectionAData);
        } else {
          // Minimal step4 entry so the PDF shows at least one Section A question.
          const cin = jsonData?.sectionA?.formData?.cin || "";
          localStorage.setItem("step4", JSON.stringify([{ id: "SectionA-CIN", module: "Section A", title: "Section A", question: "CIN", answer: cin }]));
        }

        // Load Section B data if available
        if (jsonData?.sectionB) {
          saveSectionData(editReportId, "B", jsonData.sectionB);
        }

        // Load Section C data if available
        if (jsonData?.sectionC) {
          saveSectionData(editReportId, "C", jsonData.sectionC);
        }

        // Load generateReport (step4) data if available from blob
        if (jsonData?.generateReport && Array.isArray(jsonData.generateReport) && jsonData.generateReport.length > 0) {
          localStorage.setItem("step4", JSON.stringify(jsonData.generateReport));
        }
      } catch (error) {
        console.error("Error loading BRSR data.json for edit:", error);
      } finally {
        setLoading(false);
      }
    };

    loadBrsrForEdit();
  }, [isEditMode, editReportId]);

  var myHeaders = new Headers();
  myHeaders.append("username", username);

  var requestOptions = {
    method: "GET",
    headers: myHeaders,
    redirect: "follow",
  };

  async function fetchLegalEntity() {
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}list-entities`, requestOptions);
      if (response.status === 200) {
        const responseJson = await response.json();
        if (responseJson.success == false) {
          setActiveLegalEntity("");
        } else if (responseJson.status == 400) {
          setActiveLegalEntity("");
        } else {
          setActiveLegalEntity(responseJson.data.filter((item) => item.isActive === true));
        }
      } else {
        setActiveLegalEntity("");
      }
    } catch (error) {
      console.error(error);
    }
  }

  async function fetchExistingReports() {
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}allreports`, {
        method: "GET",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          companyid: companyid,
          username: username,
        },
      });
      if (response.status !== 200) {
        setExistingReports([]);
        return;
      }
      const responseJson = await response.json();
      if (responseJson?.success === false || responseJson?.status === 400 || !Array.isArray(responseJson?.data)) {
        setExistingReports([]);
        return;
      }
      setExistingReports(responseJson.data);
    } catch (error) {
      console.error(error);
      setExistingReports([]);
    }
  }

  const isDuplicateReportName = (reportName) => {
    const normalizedName = (reportName || "").trim().toLowerCase();
    if (!normalizedName) return false;

    return existingReports.some((report) => {
      const existingName = (report?.reportTitle || "").toString().trim().toLowerCase();
      if (!existingName || existingName !== normalizedName) return false;

      // In edit mode, allow the current report's own name.
      if (isEditMode) {
        const existingId = String(report?.rowKey || report?.reportId || "");
        const currentId = String(editReportId || reportId || "");
        if (existingId && currentId && existingId === currentId) return false;
      }
      return true;
    });
  };

  const formatDate = (date) => {
    return date ? date.toLocaleDateString() : "";
  };

  async function downloadBrsrBlobJson(blobPath) {
    try {
      const res = await fetch(`${process.env.REACT_APP_BASE_API_URL}GetSASToken`, {
        method: "GET",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
      });
      if (res.status !== 200) return null;
      const responseJson = await res.json();
      const connectionString = responseJson.data;
      const blobServiceClient = new BlobServiceClient(connectionString);
      const containerClient = blobServiceClient.getContainerClient("ecoprismfilecontainer");
      const blobClient = containerClient.getBlobClient(blobPath);
      const downloadBlockBlobResponse = await blobClient.download(0);
      const blob = await downloadBlockBlobResponse.blobBody;
      const downloadedText = await blob.text();
      if (!downloadedText) return null;
      return JSON.parse(downloadedText);
    } catch (error) {
      console.warn("downloadBrsrBlobJson failed:", error);
      return null;
    }
  }

  async function uploadJsonFile(containerName, folderName, fileName, jsonData, brsrOptions) {
    try {
      let res = await fetch(`${process.env.REACT_APP_BASE_API_URL}GetSASToken`, {
        method: "GET",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
      });

      if (res.status === 200) {
        const responseJson = await res.json();
        const connectionString = responseJson.data;

        const blobServiceClient = new BlobServiceClient(connectionString);
        const containerClient = blobServiceClient.getContainerClient(containerName);
        const isBRSRPath = brsrOptions && typeof folderName === "string" && folderName.includes("reports/brsr");
        const blobName = isBRSRPath ? folderName : `${folderName}/${fileName}.json`;
        const blobClient = containerClient.getBlobClient(blobName);
        const blockBlobClient = blobClient.getBlockBlobClient();

        const arrayBuffer = new TextEncoder().encode(JSON.stringify(jsonData));

        const uploadResponse = await blockBlobClient.uploadBrowserData(arrayBuffer.buffer, {
          blobHTTPHeaders: { blobContentType: "application/json" },
          blockSize: 4 * 1024 * 1024,
          concurrency: 20,
          onProgress: (ev) => console.log(ev.loadedBytes),
        });
        if (uploadResponse) {
          console.log(`JSON file uploaded successfully to ${blobName}`);
          console.log(`Blob URL: ${blobClient.url}`);
          if (!isBRSRPath) {
            localStorage.setItem("hash", fileName);
          }
          localStorage.setItem("bloburl", blobClient.url);

          const timelineDate = timeline.toDate();
          const year = timelineDate.getFullYear();
          const month = String(timelineDate.getMonth() + 1).padStart(2, "0");
          const day = String(timelineDate.getDate()).padStart(2, "0");
          const formattedTimeline = `${year}-${month}-${day}`;

          const organizationValue = localStorage.getItem("organization");
          const bloburl = localStorage.getItem("bloburl");
          const username = localStorage.getItem("userName");
          const requestOptions = {
            reportId: isBRSRPath && brsrOptions?.reportId ? brsrOptions.reportId : fileName || brsrOptions?.reportId,
            reportTitle: name,
            companyName: organizationValue,
            companyId: companyid,
            blobStorageLocation: bloburl,
            updatedBy: username,
            reportStatus: "Active",
            updateComment: "Test Comment",
            completionTimeline: formattedTimeline,
            financialYear: financialYear,
          };
          if (isBRSRPath && brsrOptions) {
            requestOptions.reportType = brsrOptions.type || "BRSR";
            requestOptions.reportingOn = brsrOptions.reportingOn ?? "";
            requestOptions.legalEntity = Array.isArray(legalEntity) ? legalEntity.join(", ") : legalEntity || "";
            requestOptions.revenuePreviousFY = brsrOptions.revenuePreviousFY ?? "";
            requestOptions.revenueCurrentFY = brsrOptions.revenueCurrentFY ?? "";
          }
          const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}save-report`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              username: localStorage.getItem("userName"),
            },
            body: JSON.stringify(requestOptions),
          });
          const data = await response.json();
          if (data.success == false) {
          } else if (data.status == 400) {
          } else {
          }
        }
      } else {
        console.error("Failed to fetch SAS token.");
      }
    } catch (error) {
      console.error("Error uploading JSON file:", error);
    }
  }

  const validateForm = () => {
    let formErrors = {};
    if (!name) formErrors.reportName = "Report Name is required";
    if ((name || "").trim() && isDuplicateReportName(name)) {
      formErrors.reportName = "Report name already exists. Please enter a unique report name.";
    }
    if (!timeline) formErrors.completionTimeline = "Completion Timeline is required";
    if (!legalEntity || legalEntity.length === 0) {
      formErrors.legalEntity = "Legal Entity is required";
    }
    if (!financialYear) formErrors.financialYear = "Financial Year is required";
    if (!reportingOn) formErrors.reportingOn = "Reporting On is required";
    if (reportingOn === "BRSR + Core") {
      const previousFY = getPreviousFinancialYear(financialYear);
      const currentFY = formatFinancialYearForDisplay(financialYear);

      if (!revenuePreviousFY || revenuePreviousFY.trim() === "") {
        formErrors.revenuePreviousFY = `Revenue for FY ${previousFY} is required`;
      } else if (isNaN(revenuePreviousFY) || parseFloat(revenuePreviousFY) < 0) {
        formErrors.revenuePreviousFY = `Revenue for FY ${previousFY} must be a valid number`;
      }
      if (!revenueCurrentFY || revenueCurrentFY.trim() === "") {
        formErrors.revenueCurrentFY = `Revenue for FY ${currentFY} is required`;
      } else if (isNaN(revenueCurrentFY) || parseFloat(revenueCurrentFY) < 0) {
        formErrors.revenueCurrentFY = `Revenue for FY ${currentFY} must be a valid number`;
      }
    }
    console.log(formErrors);
    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    const completionTimeline = selectedDate || null;

    const newReport = {
      reportName: name,
      framework: "BRSR",
      status: "In Progress",
      completionTimeline: completionTimeline,
      financialYear: financialYear,
      reportingOn: reportingOn,
      revenuePreviousFY: revenuePreviousFY,
      revenueCurrentFY: revenueCurrentFY,
      creator: "Unknown",
    };

    const localReports = JSON.parse(localStorage.getItem("savedReports")) || [];
    const updatedReports = [...localReports, newReport];
    localStorage.setItem("savedReports", JSON.stringify(updatedReports));

    if (timeline && dayjs.isDayjs(timeline)) {
      const timelineDate = timeline.toDate();
      if (timelineDate instanceof Date) {
        const year = timelineDate.getFullYear();
        const month = String(timelineDate.getMonth() + 1).padStart(2, "0");
        const day = String(timelineDate.getDate()).padStart(2, "0");
        const formattedTimeline = `${year}-${month}-${day}`;

        const rdata = {
          name: name,
          timeline: formattedTimeline,
          timeline1: selectedDate,
          legalEntity: legalEntity,
          financialYear: financialYear,
          reportingOn: reportingOn,
          revenuePreviousFY: revenuePreviousFY,
          revenueCurrentFY: revenueCurrentFY,
        };
        console.log(rdata);

        const id = reportId || crypto.randomUUID();
        if (!reportId) {
          setReportId(id);
          localStorage.setItem("brsr_report_id", id);

          // Clear old generic section data to prevent cross-contamination between reports
          localStorage.removeItem("brsr_sectionA_data");
          localStorage.removeItem("brsr_sectionB_data");
          localStorage.removeItem("brsr_sectionC_data");
          localStorage.removeItem("step4");
        }

        const reportType = reportingOn === "BRSR + Core" ? "BRSR_CORE" : "BRSR";
        const brsrBlobPath = `${companyid}/reports/brsr/${id}/data.json`;
        // In edit mode, preserve section/generateReport/logo already on the blob so Start Save
        // does not wipe Section A/B/C that were previously saved from Preview.
        let payloadToUpload = { reportId: id, ...rdata };
        if (isEditMode) {
          try {
            const existingBlob = await downloadBrsrBlobJson(brsrBlobPath);
            if (existingBlob && typeof existingBlob === "object") {
              payloadToUpload = {
                ...existingBlob,
                ...rdata,
                reportId: id,
                sectionA: existingBlob.sectionA || {},
                sectionB: existingBlob.sectionB || {},
                sectionC: existingBlob.sectionC || {},
                generateReport: Array.isArray(existingBlob.generateReport) ? existingBlob.generateReport : [],
                logo: existingBlob.logo ?? null,
              };
            }
          } catch (mergeErr) {
            console.warn("Unable to merge existing BRSR blob on Start save:", mergeErr);
          }
        }
        const brsrOptions = {
          reportId: id,
          type: reportType,
          reportingOn: reportingOn || "",
          revenuePreviousFY: revenuePreviousFY || "",
          revenueCurrentFY: revenueCurrentFY || "",
        };
        await uploadJsonFile("ecoprismfilecontainer", brsrBlobPath, null, payloadToUpload, brsrOptions);

        localStorage.setItem("step1", JSON.stringify(rdata));
        localStorage.setItem("report_name", name);
        setSavedReports(updatedReports);

        // In edit mode, keep the data filled for possible edits
        // In new mode, clear the form for potential new entries
        if (!isEditMode) {
          setName("");
          setSelectedDate(null);
          setTimeline("");
          setLegalEntity("");
          setFinancialYear("");
          setRevenuePreviousFY("");
          setRevenueCurrentFY("");
        }

        console.log("Data being saved:", newReport);

        // Navigate to repository with reportId in URL (always include reportId for proper data isolation)
        const finalReportId = reportId || id;
        navigate(isEditMode ? `/brsr/repository?mode=edit&reportId=${encodeURIComponent(editReportId)}` : `/brsr/repository?mode=new&reportId=${encodeURIComponent(finalReportId)}`);
      } else {
        console.error("Timeline is not a valid Date object:", timeline);
      }
    } else {
      console.error("Timeline is not valid:", timeline);
    }
  };

  const handleMultiSelectChange = (selectedValues) => {
    setLegalEntity(Array.isArray(selectedValues) ? selectedValues : []);
  };

  const handleDateChange = (date, setter) => {
    if (!date) {
      return;
    }
    if (setter === setSelectedDate) {
      setSelectedDate(date);
      setTimeline(date);
      setFormData({
        ...formData,
        completionTimeline: date,
      });
    } else {
      setter(date);
    }
  };

  // Helper function to calculate previous financial year
  const getPreviousFinancialYear = (fyValue) => {
    if (!fyValue) return "";

    // Parse format "YYYY-YY" (e.g., "2024-25")
    const parts = fyValue.split("-");
    if (parts.length !== 2) return "";

    const startYear = parseInt(parts[0]);
    const prevStartYear = startYear - 1;
    const prevEndYear = startYear; // Previous FY ends at current start year

    // Format: "YYYY-YY"
    const prevEndYearShort = String(prevEndYear).slice(-2);
    return `${prevStartYear}-${prevEndYearShort}`;
  };

  // Helper function to format financial year for display (e.g., "2024-25" -> "2024-25")
  const formatFinancialYearForDisplay = (fyValue) => {
    if (!fyValue) return "";
    return fyValue; // Already in "YYYY-YY" format
  };

  const handleFinancialYearChange = (e) => {
    if (e.value) {
      setFinancialYear(e.value);
      setErrors((prevErrors) => ({ ...prevErrors, financialYear: "" }));
    } else {
      setFinancialYear("");
    }
  };

  const handleReportingOnChange = (event) => {
    setReportingOn(event.target.value);
    setErrors((prevErrors) => ({ ...prevErrors, reportingOn: "" }));
  };

  const handleRevenueChange = (which, value) => {
    // Only allow numbers (including decimal points for currency)
    const numericValue = value.replace(/[^0-9.]/g, "");
    // Prevent multiple decimal points
    const parts = numericValue.split(".");
    const finalValue = parts.length > 2 ? parts[0] + "." + parts.slice(1).join("") : numericValue;

    if (which === "previous") {
      setRevenuePreviousFY(finalValue);
      setErrors((prevErrors) => ({ ...prevErrors, revenuePreviousFY: "" }));
    } else if (which === "current") {
      setRevenueCurrentFY(finalValue);
      setErrors((prevErrors) => ({ ...prevErrors, revenueCurrentFY: "" }));
    }
  };

  useEffect(() => {
    fetchLegalEntity();
    fetchExistingReports();
    localStorage.setItem("popup1", "true");
    localStorage.setItem("popup2", "true");
    localStorage.setItem("popup3", "true");
  }, []);

  const legalentity = activelegalentity && activelegalentity.map((status) => status.name);

  const renderHeader = () => {
    const loginType = localStorage.getItem("loginType");
    if (loginType === "AAD") {
      return <AADHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
    }
    return <LoginHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
  };

  return (
    <div id="wrapper">
      <div>
        {loading && (
          <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
      </div>
      {renderHeader()}
      <SideNavigation isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div id="page-content-wrapper" className={isSidebarOpen ? "page-content-wrapper-pd-0" : "page-wrapper"}>
        <div className="container formpd">
          <div className="">
            <div className="row">
              <div className="steps__containt-hader col-md-6">
                <h1 className="title">BRSR</h1>
                <div role="presentation">
                  <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                    <Link underline="hover" color="inherit" href="/standards">
                      Reports
                    </Link>
                    <Link underline="hover" color="inherit" href="/standards">
                      Standards & Frameworks
                    </Link>
                    <Typography color="text.primary">BRSR</Typography>
                  </Breadcrumbs>
                </div>
              </div>
              <div className="stepper-box col-md-6">
                <Stepper activeStep={0} alternativeLabel className="brsr-stepper">
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel className="step-name">{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </div>
            </div>
          </div>
          <div>
            <div className="row csrd-top">
              <div className="csrd-text col-md-12">
                <div className="row brsr-row-equal-height">
                  <div className="col-md-2 brsr-image-col">
                    <div className="brsr-image-container">
                      <img src="/assets/img/reports/brsr.webp" alt="BRSR" className="brsr-image" />
                    </div>
                  </div>
                  <div className="col-md-10 brsr-text-col">
                    <h2>Business Responsibility and Sustainability Reporting</h2>
                    <p>
                      is an integrated reporting framework. Its purpose is to increase the level of reporting on environmental, social, and governance (ESG) performance. BRSR requires enterprises to report ESG performance{" "}
                      <a href="https://www.sebi.gov.in/sebi_data/commondocs/jul-2023/Annexure_II-Updated-BRSR_p.PDF" target="_blank" rel="noopener noreferrer">
                        &lt;Read More&gt;
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="csrd-form">
              <form>
                <h2>NEW REPORT</h2>
                <div className="row gx-5 gy-3">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="formlable" htmlFor="reportName">
                        Report Name <span className="mark-form">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        disabled={isEditMode}
                        className={`form-control ${errors.reportName ? "is-invalid" : ""}`}
                        name="reportName"
                        placeholder="Enter the required report name"
                        value={name}
                        onChange={(e) => {
                          const input = e.target.value;

                          if (input.length <= 30) {
                            setName(input);
                            localStorage.setItem("report_name", input);
                            if ((input || "").trim() && isDuplicateReportName(input)) {
                              setErrors((prevErrors) => ({
                                ...prevErrors,
                                reportName: "Report name already exists. Please enter a unique report name.",
                              }));
                            } else {
                              setErrors((prevErrors) => ({ ...prevErrors, reportName: "" }));
                            }
                          } else {
                            setErrors((prevErrors) => ({
                              ...prevErrors,
                              reportName: "Report name cannot exceed 30 characters",
                            }));
                          }
                        }}
                      />

                      {errors.reportName && <div className="invalid-feedback">{errors.reportName}</div>}
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="formlable" htmlFor="completionTimeline">
                        Completion Timeline <span className="mark-form">*</span>
                      </label>
                      <div className="custom-date-picker date-picker">
                        <DatePicker
                          id="completionTimeline"
                          value={selectedDate}
                          onChange={(date) => {
                            handleDateChange(date, setSelectedDate);
                            setErrors((prevErrors) => ({ ...prevErrors, completionTimeline: "" }));
                          }}
                          className={`calendar-form form-control ${errors.completionTimeline ? "is-invalid" : ""}`}
                          placeholder="Select Date"
                          format="YYYY-MM-DD"
                          disabledDate={(current) => current && current < new Date(new Date().setHours(0, 0, 0, 0))}
                          allowClear
                          suffixIcon={<BiSolidCalendar />}
                        />
                        {errors.completionTimeline && <div className="invalid-feedback">{errors.completionTimeline}</div>}
                      </div>
                    </div>
                  </div>
                </div>

                <h2>SPECIFY THE REPORTING BOUNDARY</h2>
                <div className="row gx-5 gy-3">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="formlable" htmlFor="legalEntity">
                        Legal Entity <span className="mark-form">*</span>
                      </label>
                      <MultiSelect
                        id="legalEntity"
                        name="legalEntity"
                        className={`wd-100 custom-multiselect ${errors.legalEntity ? "is-invalid" : ""}`}
                        value={legalEntity}
                        onChange={(e) => {
                          handleMultiSelectChange(e.value);
                          setErrors((prevErrors) => ({
                            ...prevErrors,
                            legalEntity: "",
                          }));
                        }}
                        options={legalentity}
                        placeholder={legalEntity.length > 0 ? `${legalEntity.length} items selected` : "Select Legal Entity"}
                        maxSelectedLabels={0}
                        showClear={false}
                        showSelectAll={true}
                        selectAllLabel="Select All"
                        style={{ "--placeholder-font-size": "16px", height: "40px" }}
                      />

                      {errors.legalEntity && <div className="invalid-feedback">{errors.legalEntity}</div>}

                      <div className="selected-tags-container">
                        {Array.isArray(legalEntity) &&
                          legalEntity.map((entity, index) => (
                            <span key={index} className="tag">
                              {entity}
                              <button
                                onClick={() => {
                                  const updatedLegalEntity = legalEntity.filter((item) => item !== entity);
                                  setLegalEntity(updatedLegalEntity);
                                }}
                                className="remove-btn"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="formlable" htmlFor="financialYear">
                        Financial Year <span className="mark-form">*</span>
                      </label>
                      <Dropdown id="financialYear" value={financialYear} options={financialYearOptions} onChange={handleFinancialYearChange} placeholder="Select Financial Year" className={`wd-100 ${errors.financialYear ? "is-invalid" : ""}`} style={{ height: "40px" }} optionLabel="label" optionValue="value" />
                      {errors.financialYear && <div className="invalid-feedback">{errors.financialYear}</div>}
                    </div>
                  </div>
                </div>
                <div className="row gx-5 gy-3">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="formlable">
                        Reporting On <span className="mark-form">*</span>
                      </label>
                      <div>
                        <FormControl component="fieldset" error={!!errors.reportingOn}>
                          <RadioGroup row value={reportingOn} onChange={handleReportingOnChange} name="reportingOn">
                            <FormControlLabel value="BRSR" control={<Radio />} label="BRSR" />
                            <FormControlLabel
                              value="BRSR + Core"
                              control={<Radio />}
                              label={
                                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                                  BRSR + Core
                                  <Tooltip title={<span style={{ fontSize: "13px", lineHeight: "1.5" }}>BRSR Core is a subset of standardized ESG Key Performance Indicators (KPIs) embedded within the revised BRSR format. These KPIs span 9 ESG attributes and are primarily mapped to some Essential indicators under Section C of BRSR. Disclosure and assurance of BRSR Core KPIs are mandated for the top 1000 listed entities from FY 2026-27 onward.</span>} placement="top" arrow>
                                    <img src="/assets/img/reports/info.svg" alt="Info" style={{ width: "16px", height: "16px", cursor: "pointer", verticalAlign: "middle" }} />
                                  </Tooltip>
                                </span>
                              }
                            />
                          </RadioGroup>
                        </FormControl>
                      </div>
                      {errors.reportingOn && (
                        <div className="invalid-feedback" style={{ display: "block", color: "#dc3545" }}>
                          {errors.reportingOn}
                        </div>
                      )}
                    </div>
                  </div>
                  {reportingOn === "BRSR + Core" && (
                    <div className="col-md-6">
                      <div className="form-group">
                        <label className="formlable" htmlFor="revenuePreviousFY">
                          Revenue (INR) <span className="mark-form">*</span>
                        </label>
                        <div className="row gx-2">
                          <div className="col-md-6">
                            <label className="formlable" htmlFor="revenuePreviousFY">
                              FY {financialYear ? getPreviousFinancialYear(financialYear) : "Previous FY"}
                            </label>
                            <input type="text" className={`form-control ${errors.revenuePreviousFY ? "is-invalid" : ""}`} name="revenuePreviousFY" placeholder={financialYear ? `Enter Revenue for FY ${getPreviousFinancialYear(financialYear)}` : "Enter Revenue for FY (Previous FY)"} value={revenuePreviousFY} onChange={(e) => handleRevenueChange("previous", e.target.value)} />
                            {errors.revenuePreviousFY && <div className="invalid-feedback">{errors.revenuePreviousFY}</div>}
                          </div>
                          <div className="col-md-6">
                            <label className="formlable" htmlFor="revenueCurrentFY">
                              FY {financialYear ? formatFinancialYearForDisplay(financialYear) : "Selected FY"}
                            </label>
                            <input type="text" className={`form-control ${errors.revenueCurrentFY ? "is-invalid" : ""}`} name="revenueCurrentFY" placeholder={financialYear ? `Enter Revenue for FY ${formatFinancialYearForDisplay(financialYear)}` : "Enter Revenue for FY (Selected FY)"} value={revenueCurrentFY} onChange={(e) => handleRevenueChange("current", e.target.value)} />
                            {errors.revenueCurrentFY && <div className="invalid-feedback">{errors.revenueCurrentFY}</div>}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="row justify-content-end pt-3">
                  <div className="col-auto">
                    <Button onClick={() => navigate("/standards")} variant="outlined" className="outline-btn edit-action-cancel-btn" size="medium" type="button">
                      Cancel
                    </Button>
                  </div>
                  <div className="col-auto">
                    <PermissionTooltip disabled={!canStartAssessment} reason="You don't have permission to save this report">
                      <Button onClick={handleSave} disabled={!canStartAssessment} className="next-button edit-action-save-btn" variant="contained" size="medium">
                        Save
                      </Button>
                    </PermissionTooltip>
                  </div>
                  {isEditMode && (
                    <div className="col-auto">
                      <Button onClick={() => navigate(`/brsr/repository?reportId=${encodeURIComponent(reportId)}`)} variant="outlined" className="outline-btn edit-action-next-btn" size="medium" type="button">
                        Next
                      </Button>
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
